import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const paragraphs = [
  "This is where Revelation Meets Transformation as God’s servant Prophet Kevin makes the revelation known to you through Wisdom given to him by God.",
  "Wisdom is the good sense of judgment and insight that makes you see things from a better perspective. [Proverbs 2:6-11]",
  "If you don't sharpen your axe head, though you apply more strength, you will not have speedy results.",
  "When you are afraid of rejection, you will often put yourself under pressure to gain acceptance from people who do not care about your sacrifices.",
  "The easiest way to wear yourself out is to labor to please ungrateful people, because nothing you do or give will ever be good enough in their eyes.",
  "The desire to please everyone is rooted in the fear of rejection. Until you understand that not everyone will accept you, you will never be at peace.",
  "Some people have set their standards for you so high that neither your life nor death can satisfy them. Stop trying to please them and be at peace.",
  "If people show ingratitude once, just know they will do it over and over again because ingratitude is not an isolated event, it is a culture. You don't need to drink the whole sea before you believe that the water is salty."
];

const SLIDE_TIME = 15000;

const AppSidebar = ({ title = "Exploring Center Coming Soon...", links = [] }) => {
  // ===== TEXT SLIDER STATE =====
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentText, setCurrentText] = useState(paragraphs[0]);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState(1);

  const intervalRef = useRef(null);
  const progressRef = useRef(null);
  const elapsedRef = useRef(0);
  const lastStartTime = useRef(0);

  const nextSlideRef = useRef(null);
  const startTimerRef = useRef(null);

  // ===== SHARE FUNCTION =====
  const handleShare = async () => {
    const shareMessage = `${currentText}\n\nFor more wisdom quotes visit the official website of Prophet Kevin N:\n${window.location.href}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Wisdom Quote",
          text: shareMessage
        });
      } catch {}
    } else {
      alert("Sharing is not supported on this device.");
    }
  };

  // ===== NEXT / PREV SLIDE =====
  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % paragraphs.length);
    startTimerRef.current();
  };
  nextSlideRef.current = nextSlide;

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + paragraphs.length) % paragraphs.length);
    startTimerRef.current();
  };

  // ===== TIMER HANDLERS =====
  const startTimer = () => {
    elapsedRef.current = 0;
    setProgress(0);
    lastStartTime.current = Date.now();

    clearInterval(intervalRef.current);
    clearInterval(progressRef.current);

    intervalRef.current = setTimeout(() => {
      nextSlideRef.current();
    }, SLIDE_TIME);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - lastStartTime.current;
      elapsedRef.current = elapsed;
      setProgress((elapsed / SLIDE_TIME) * 100);
    }, 100);
  };
  startTimerRef.current = startTimer;

  const pauseTimer = () => {
    clearInterval(intervalRef.current);
    clearInterval(progressRef.current);
  };

  const resumeTimer = () => {
    lastStartTime.current = Date.now() - elapsedRef.current;
    clearInterval(intervalRef.current);
    clearInterval(progressRef.current);
    const remaining = SLIDE_TIME - elapsedRef.current;
    intervalRef.current = setTimeout(() => {
      nextSlideRef.current();
    }, remaining);
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - lastStartTime.current;
      elapsedRef.current = elapsed;
      setProgress((elapsed / SLIDE_TIME) * 100);
    }, 100);
  };

  // ===== TEXT UPDATE =====
  useEffect(() => {
    setCurrentText(paragraphs[currentIndex]);
  }, [currentIndex]);

  useEffect(() => {
    startTimer();
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(progressRef.current);
    };
  }, []);

  return (
    <div className="mt-[50px] p-1">
      {/* ===== TEXT SLIDER ===== */}
      <div
        className="relative bg-accent border border-border w-full h-[180px] overflow-hidden rounded mx-auto flex flex-col items-center justify-center"
        style={{ userSelect: "none" }}
        onMouseEnter={pauseTimer}
        onMouseLeave={resumeTimer}
        onTouchStart={(e) => {
          e.preventDefault();
          pauseTimer();
        }}
        onTouchEnd={resumeTimer}
        onTouchCancel={resumeTimer}
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onPaste={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Share Button */}
        <button
          onClick={handleShare}
          className="absolute top-[1px] left-0 text-text bg-bg px-2 py-1 m-1 rounded text-sm font-semibold"
        >
          Share
        </button>

        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-[2px]">
          <div
            className="h-full bg-text transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Animated Text */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentText}
            className="text-center leading-relaxed font-bold text-text mt-4 flex flex-col items-center"
            initial={{ x: direction * 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -direction * 300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 80, damping: 20 }}
          >
            {currentText.split(/(?<=[.?!])\s+/).map((sentence, i) => (
              <motion.span
                key={sentence + i}
                className="mb-1 block"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ delay: i * 0.3, type: "spring", stiffness: 80, damping: 20 }}
              >
                {sentence}
              </motion.span>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute top-[0px] left-[100px] text-primary text-4xl font-bold rounded-full w-10 h-10"
        >
          ‹
        </button>
        <button
          onClick={nextSlide}
          className="absolute top-[0px] right-[100px] text-primary text-4xl font-bold rounded-full w-10 h-10"
        >
          ›
        </button>
      </div>

      <h2 className="text-center font-bold text-secondary">
        Wisdom Quotes By Prophet Kevin N.
      </h2>

      {/* ===== SIDEBAR LINKS ===== */}
      <div>
        <h2 className="text-lg text-text font-bold mb-4">{title}</h2>
        {links.map((link, index) => (
          <Link
            key={index}
            to={link.to}
            className="block hover:text-hover transition-colors"
          >
            {link.icon && <span className="mr-2">{link.icon}</span>}
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AppSidebar;
